/**
 * 9.10 step 6: MetricsDashboard — VPS health + metrics + alerts.
 *
 * Stack: pure CSS/SVG sparklines + radial gauges (без recharts чтобы не тащить +200 KB в bundle).
 * Polling каждые 5s через fetchDevopsMetrics. История последних 30 точек хранится локально.
 *
 * Структура:
 *   - Header: статус-индикатор (ok/warning/critical) + uptime + auto-refresh toggle.
 *   - Alerts panel (если count > 0): карточки по severity.
 *   - Gauges row: CPU / RAM / Disk (макс. mount) — radial.
 *   - Sparklines row: CPU / RAM timeline (последние 30s).
 *   - Disk mounts table: per-mount usage_percent.
 *   - Top processes table: top-5 по RAM.
 */

import { useEffect, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cpu,
  HardDrive,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Server,
  XCircle,
} from "lucide-react";
import {
  fetchDevopsHealth,
  fetchDevopsMetrics,
  fetchDevopsAlerts,
  type DevopsAlert,
  type DevopsHealthResponse,
  type DevopsMetricsSnapshot,
} from "../../lib/api";
import { cn } from "../../lib/cn";

const POLL_INTERVAL_MS = 5000;
const HISTORY_SIZE = 30;

interface HistoryPoint {
  t: number;
  cpu: number;
  ram: number;
}

export function MetricsDashboard() {
  const [health, setHealth] = useState<DevopsHealthResponse | null>(null);
  const [snapshot, setSnapshot] = useState<DevopsMetricsSnapshot | null>(null);
  const [alerts, setAlerts] = useState<DevopsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const intervalRef = useRef<number | null>(null);

  async function refresh() {
    setError(null);
    try {
      const h = await fetchDevopsHealth();
      setHealth(h);

      if (!h.vps_connected) {
        setSnapshot(null);
        setAlerts([]);
        setLoading(false);
        return;
      }

      const m = await fetchDevopsMetrics();
      setSnapshot(m.snapshot);
      setHistory((prev) => {
        const next: HistoryPoint = {
          t: Date.now(),
          cpu: m.snapshot.cpu.usage_percent,
          ram: m.snapshot.ram.usage_percent,
        };
        const arr = [...prev, next];
        return arr.length > HISTORY_SIZE ? arr.slice(-HISTORY_SIZE) : arr;
      });

      const a = await fetchDevopsAlerts();
      setAlerts(a.alerts);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось загрузить метрики");
    } finally {
      setLoading(false);
    }
  }

  // Mount + poll loop
  useEffect(() => {
    void refresh();
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = window.setInterval(() => {
        void refresh();
      }, POLL_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  // === Render branches ===

  if (loading && !snapshot && !health) {
    return (
      <div className="p-6 grid place-items-center h-full">
        <Loader2 className="size-8 animate-spin text-fg-muted" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-card border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
          <XCircle className="size-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-small font-medium text-red-300">Ошибка загрузки метрик</p>
            <p className="text-small text-fg-muted mt-1">{error}</p>
            <button onClick={refresh} className="btn-ghost text-small mt-3">
              <RefreshCw className="size-3.5 inline mr-1" /> Повторить
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (health && !health.vps_connected) {
    return (
      <div className="p-6">
        <div className="rounded-card border border-border/60 p-5 flex items-start gap-3">
          <Server className="size-6 text-fg-muted shrink-0" />
          <div className="flex-1">
            <p className="text-body font-medium">VPS не подключён</p>
            <p className="text-small text-fg-muted mt-1">
              Подключите VPS в разделе «VPS» сайдбара, чтобы видеть метрики и алерты.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!snapshot) return null;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <StatusIndicator status={health?.status || "unknown"} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-mono text-fg-subtle">
            DevOps · мониторинг
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            <span className="grad-text">{formatUptime(snapshot.uptime_seconds)}</span>
            <span className="ml-2 text-fg text-base font-medium align-middle">
              uptime · {snapshot.cpu.n_cpus} CPU
            </span>
          </h1>
          <p className="text-small text-fg-muted mt-0.5">
            Polling каждые 5s
            {alerts.length > 0 && (
              <span className="ml-2 text-warning font-medium">· {alerts.length} alert{alerts.length > 1 ? "s" : ""}</span>
            )}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border/60 bg-bg-card hover:bg-bg-subtle text-small text-fg-muted hover:text-fg transition-colors"
            title={autoRefresh ? "Остановить автообновление" : "Запустить автообновление"}
          >
            {autoRefresh ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
            {autoRefresh ? "Авто 5s" : "Пауза"}
          </button>
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border/60 bg-bg-card hover:bg-bg-subtle text-small text-fg-muted hover:text-fg transition-colors"
          >
            <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
            <span className="hidden sm:inline">Обновить</span>
          </button>
        </div>
      </div>

      {/* Alerts panel */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <AlertCard key={i} alert={a} />
          ))}
        </div>
      )}

      {/* Gauges row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <GaugeCard
          icon={<Cpu className="size-4 text-blue-400" />}
          title="CPU"
          value={snapshot.cpu.usage_percent}
          subtitle={`Load 1m: ${snapshot.cpu.load_avg_1m.toFixed(2)} (${snapshot.cpu.n_cpus} CPU)`}
          warningAt={75}
          criticalAt={90}
        />
        <GaugeCard
          icon={<Activity className="size-4 text-green-400" />}
          title="RAM"
          value={snapshot.ram.usage_percent}
          subtitle={`${formatMb(snapshot.ram.used_mb)} / ${formatMb(snapshot.ram.total_mb)}`}
          warningAt={80}
          criticalAt={95}
        />
        <GaugeCard
          icon={<HardDrive className="size-4 text-violet-400" />}
          title="Disk (макс)"
          value={maxDiskPct(snapshot.disks)}
          subtitle={`${snapshot.disks.length} mount${snapshot.disks.length === 1 ? "" : "ов"}`}
          warningAt={80}
          criticalAt={95}
        />
      </div>

      {/* Sparklines */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SparklineCard
          title="CPU usage (последние 30 точек)"
          color="rgb(59 130 246)"
          values={history.map((p) => p.cpu)}
          unit="%"
        />
        <SparklineCard
          title="RAM usage (последние 30 точек)"
          color="rgb(34 197 94)"
          values={history.map((p) => p.ram)}
          unit="%"
        />
      </div>

      {/* Disks */}
      <div className="rounded-card border border-border/60 p-4">
        <h3 className="text-small font-medium mb-3 flex items-center gap-2">
          <HardDrive className="size-4 text-violet-400" /> Диски ({snapshot.disks.length})
        </h3>
        <div className="space-y-2">
          {snapshot.disks.map((d, i) => (
            <DiskRow key={i} disk={d} />
          ))}
          {snapshot.disks.length === 0 && (
            <p className="text-small text-fg-muted">Нет данных</p>
          )}
        </div>
      </div>

      {/* Top processes */}
      {snapshot.top_processes.length > 0 && (
        <div className="rounded-card border border-border/60 p-4">
          <h3 className="text-small font-medium mb-3">Top-5 процессов по RAM</h3>
          <table className="w-full text-small">
            <thead>
              <tr className="text-left text-fg-muted text-[0.75rem] border-b border-border/40">
                <th className="py-2">PID</th>
                <th>USER</th>
                <th>CPU %</th>
                <th>RAM %</th>
                <th>Команда</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.top_processes.map((p, i) => (
                <tr key={i} className="border-b border-border/20 last:border-0">
                  <td className="py-2 font-mono text-fg-muted">{p.pid}</td>
                  <td className="font-mono">{p.user}</td>
                  <td className={p.cpu_percent > 50 ? "text-orange-300" : ""}>
                    {p.cpu_percent.toFixed(1)}
                  </td>
                  <td className={p.mem_percent > 30 ? "text-orange-300" : ""}>
                    {p.mem_percent.toFixed(1)}
                  </td>
                  <td className="font-mono text-fg-muted truncate max-w-[400px]">{p.command}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Network mini-card */}
      <div className="rounded-card border border-border/60 p-4 grid grid-cols-3 gap-4 text-small">
        <div>
          <p className="text-fg-muted text-[0.75rem]">TCP established</p>
          <p className="text-heading-3 font-semibold">{snapshot.network.tcp_established}</p>
        </div>
        <div>
          <p className="text-fg-muted text-[0.75rem]">TCP time-wait</p>
          <p className="text-heading-3 font-semibold">{snapshot.network.tcp_time_wait}</p>
        </div>
        <div>
          <p className="text-fg-muted text-[0.75rem]">UDP active</p>
          <p className="text-heading-3 font-semibold">{snapshot.network.udp_active}</p>
        </div>
      </div>

      {/* Errors (если были при сборе) */}
      {snapshot.errors.length > 0 && (
        <div className="rounded-card border border-yellow-500/40 bg-yellow-500/5 p-3">
          <p className="text-small font-medium text-yellow-300 mb-1">
            Ошибки сбора метрик ({snapshot.errors.length}):
          </p>
          <ul className="text-[0.75rem] text-fg-muted space-y-1">
            {snapshot.errors.map((e, i) => (
              <li key={i} className="font-mono">
                · {e}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// === Helpers / sub-components ===

function StatusIndicator({ status }: { status: string }) {
  const config: Record<string, { Icon: typeof CheckCircle2; cls: string; label: string }> = {
    ok: { Icon: CheckCircle2, cls: "text-green-400 bg-green-500/15", label: "OK" },
    warning: { Icon: AlertTriangle, cls: "text-yellow-400 bg-yellow-500/15", label: "Warning" },
    critical: { Icon: XCircle, cls: "text-red-400 bg-red-500/15", label: "Critical" },
    unknown: { Icon: Server, cls: "text-fg-muted bg-bg-subtle", label: "Unknown" },
  };
  const { Icon, cls, label } = config[status] || config.unknown;
  return (
    <div className={cn("size-12 rounded-card grid place-items-center", cls)} title={label}>
      <Icon className="size-6" />
    </div>
  );
}

function AlertCard({ alert }: { alert: DevopsAlert }) {
  const severityCls =
    alert.severity === "critical"
      ? "border-red-500/40 bg-red-500/10 text-red-300"
      : alert.severity === "warning"
      ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"
      : "border-blue-500/40 bg-blue-500/10 text-blue-300";
  const Icon = alert.severity === "critical" ? XCircle : AlertTriangle;
  return (
    <div className={cn("rounded-card border p-3 flex items-start gap-3", severityCls)}>
      <Icon className="size-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-small font-medium">{alert.message}</p>
        <p className="text-[0.75rem] opacity-75 mt-0.5">
          Правило <span className="font-mono">{alert.rule}</span> · значение {alert.value.toFixed(1)} ≥ порог{" "}
          {alert.threshold.toFixed(1)}
        </p>
      </div>
    </div>
  );
}

function GaugeCard({
  icon,
  title,
  value,
  subtitle,
  warningAt,
  criticalAt,
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtitle: string;
  warningAt: number;
  criticalAt: number;
}) {
  const pct = Math.max(0, Math.min(100, value));
  const color =
    pct >= criticalAt ? "rgb(239 68 68)" : pct >= warningAt ? "rgb(234 179 8)" : "rgb(34 197 94)";
  // SVG-радиальный gauge
  const r = 36;
  const c = 2 * Math.PI * r;
  const filled = (pct / 100) * c;

  return (
    <div className="rounded-card border border-border/60 p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-small font-medium">{title}</span>
      </div>
      <div className="flex items-center gap-3">
        <svg width="84" height="84" className="shrink-0 -rotate-90">
          <circle cx="42" cy="42" r={r} stroke="rgb(255 255 255 / 0.06)" strokeWidth="6" fill="none" />
          <circle
            cx="42"
            cy="42"
            r={r}
            stroke={color}
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${c}`}
            style={{ transition: "stroke-dasharray 0.5s ease" }}
          />
        </svg>
        <div>
          <p className="text-heading-2 font-semibold" style={{ color }}>
            {pct.toFixed(0)}%
          </p>
          <p className="text-[0.75rem] text-fg-muted">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function SparklineCard({
  title,
  color,
  values,
  unit,
}: {
  title: string;
  color: string;
  values: number[];
  unit: string;
}) {
  const W = 280;
  const H = 60;
  const max = Math.max(100, ...values);
  const min = 0;
  const last = values.length > 0 ? values[values.length - 1] : 0;
  const path =
    values.length === 0
      ? ""
      : values
          .map((v, i) => {
            const x = (i / Math.max(1, values.length - 1)) * W;
            const y = H - ((v - min) / Math.max(1, max - min)) * H;
            return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
          })
          .join(" ");
  const areaPath =
    values.length === 0
      ? ""
      : `${path} L ${W} ${H} L 0 ${H} Z`;

  return (
    <div className="rounded-card border border-border/60 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-small font-medium">{title}</p>
        <p className="text-small font-mono" style={{ color }}>
          {last.toFixed(1)}
          {unit}
        </p>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {areaPath && <path d={areaPath} fill={color} fillOpacity="0.12" />}
        {path && (
          <path
            d={path}
            stroke={color}
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {values.length === 0 && (
          <text
            x={W / 2}
            y={H / 2 + 4}
            textAnchor="middle"
            fill="currentColor"
            opacity="0.5"
            fontSize="11"
          >
            Сбор данных…
          </text>
        )}
      </svg>
    </div>
  );
}

function DiskRow({ disk }: { disk: DevopsMetricsSnapshot["disks"][number] }) {
  const pct = disk.usage_percent;
  const color = pct >= 95 ? "bg-red-500" : pct >= 80 ? "bg-yellow-500" : "bg-green-500";
  return (
    <div>
      <div className="flex justify-between text-small mb-1">
        <span className="font-mono">{disk.mount}</span>
        <span className="text-fg-muted">
          {disk.used_gb.toFixed(1)} / {disk.total_gb.toFixed(1)} GB ({pct.toFixed(0)}%)
        </span>
      </div>
      <div className="h-2 rounded-full bg-bg-subtle overflow-hidden">
        <div
          className={cn("h-full transition-all", color)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function maxDiskPct(disks: DevopsMetricsSnapshot["disks"]): number {
  return disks.reduce((max, d) => Math.max(max, d.usage_percent), 0);
}

function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
